import React, { useEffect, useMemo, useRef, useState } from 'react';
import type {
    ProductTableEditorViewProps,
    ProductTablePrice,
    ProductTableProduct,
    SortDirection,
    SortKey,
} from './ProductTableEditorView.types';
import { colors, styles } from './ProductTableEditorView.styles';
import {
    buildProductTableSavePayload,
    categoryColor,
    createEmptyProductForm,
    downloadProductTableDebugFiles,
    ensureAtLeastOneFeature,
    ensureAtLeastOnePrice,
    formatPrice,
    getBaseImagePathFromSample,
    getAttributes,
    getBasic,
    getDimensions,
    getDimensionsLabel,
    getDisplayName,
    getFileExtension,
    getFileExtensionFromFile,
    getFlagValue,
    getFlags,
    getIsRemoved,
    getMetaData,
    getNextNumberForPrefix,
    getPriceValue,
    getPrimaryImage,
    getProductTypeLabel,
    getRecordLabel,
    makeUniqueId,
    normalizeProductForDiff,
    slugifyValue,
    splitLines,
    type NewProductFormState,
    type NewProductImageFormState,
    type NewProductPriceFormState,
    type ProductFlagKey,
} from './ProductTableEditorView.helpers';

type UseProductTableEditorControllerArgs = Pick<
    ProductTableEditorViewProps,
    'products' | 'onProductsChange' | 'onSave' | 'onAddProduct' | 'onDeleteProduct' | 'fileNamePrefix'
>;

export const useProductTableEditorController = ({
    products,
    onProductsChange,
    onSave,
    onAddProduct,
    onDeleteProduct,
    fileNamePrefix = 'product-items',
}: UseProductTableEditorControllerArgs) => {
    const [draftProducts, setDraftProducts] = useState<ProductTableProduct[]>(products);
    const originalProductsRef = useRef<ProductTableProduct[]>(products);
    const [query, setQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [inventoryFilter, setInventoryFilter] = useState<'all' | 'with' | 'without'>('all');
    const [flagFilters, setFlagFilters] = useState({
        isFeatured: false,
        isClearance: false,
        isAusMade: false,
        isInStock: false,
    });
    const [selectedCategoryKeys, setSelectedCategoryKeys] = useState<string[]>([]);
    const [selectedTypeKeys, setSelectedTypeKeys] = useState<string[]>([]);
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'deactivated'>('all');
    const [showCountFilters, setShowCountFilters] = useState(false);
    const [sortKey, setSortKey] = useState<SortKey>('sortOrder');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [editingCell, setEditingCell] = useState<string | null>(null);
    const [lightboxSlides, setLightboxSlides] = useState<{ src: string; alt?: string }[]>([]);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [pendingDeleteProduct, setPendingDeleteProduct] = useState<ProductTableProduct | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [savingElapsedSeconds, setSavingElapsedSeconds] = useState(0);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
    const [newProductForm, setNewProductForm] = useState<NewProductFormState>(() => createEmptyProductForm());
    const imageFileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        setDraftProducts(products);
        setHasChanges(false);
    }, [products]);

    const savingStartedAtRef = useRef<number | null>(null);

    useEffect(() => {
        if (!isSaving) return;

        const startedAt = savingStartedAtRef.current || Date.now();
        savingStartedAtRef.current = startedAt;
        setSavingElapsedSeconds(0);

        const timer = window.setInterval(() => {
            setSavingElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
        }, 1000);

        return () => window.clearInterval(timer);
    }, [isSaving]);

    const productsForStatusFilter = useMemo(
        () =>
            draftProducts.filter((product) => {
                if (statusFilter === 'active') return !getIsRemoved(product);
                if (statusFilter === 'deactivated') return getIsRemoved(product);
                return true;
            }),
        [draftProducts, statusFilter],
    );

    const categorySummaries = useMemo(() => {
        const map = new Map<string, { label: string; count: number; typeKeys: Set<string> }>();
        productsForStatusFilter.forEach((product) => {
            const key = product.categoryId || product.parentId || 'uncategorised';
            const label = product.categoryName || getBasic(product).category || product.parentId || 'Uncategorised';
            const typeKey = product.productId || product.parentId || product.productSlug || product.slug || product.id;
            const entry = map.get(key) || { label, count: 0, typeKeys: new Set<string>() };
            entry.count += 1;
            entry.typeKeys.add(typeKey);
            map.set(key, entry);
        });
        return Array.from(map.entries()).map(([key, value], index) => ({
            key,
            label: value.label,
            count: value.count,
            typeCount: value.typeKeys.size,
            color: categoryColor(index),
        }));
    }, [productsForStatusFilter]);

    const productTypeSummaries = useMemo(() => {
        const map = new Map<string, { label: string; count: number; categoryKey: string; categoryLabel: string }>();
        productsForStatusFilter.forEach((product) => {
            const key = product.productId || product.parentId || product.productSlug || product.slug || product.id;
            const label = product.productName || getBasic(product).subtitle || product.productSlug || product.parentId || 'Untyped';
            const categoryKey = product.categoryId || product.parentId || 'uncategorised';
            const categoryLabel = product.categoryName || getBasic(product).category || 'Uncategorised';
            const entry = map.get(key) || { label, count: 0, categoryKey, categoryLabel };
            entry.count += 1;
            map.set(key, entry);
        });
        return Array.from(map.entries())
            .map(([key, value]) => ({ key, ...value }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [productsForStatusFilter]);

    const categoryOptions = useMemo(() => {
        const map = new Map<string, {
            key: string;
            label: string;
            categoryId?: string;
            categorySlug?: string;
            categoryName?: string;
            parentId?: string;
            productSlug?: string;
            productName?: string;
            imageBasePath?: string;
        }>();

        draftProducts.forEach((product) => {
            const parentId = product.parentId || product.productSlug || product.categoryId || product.categorySlug || 'uncategorised';
            const key = parentId;
            if (map.has(key)) return;

            const sampleImage = getPrimaryImage(product)?.original || getPrimaryImage(product)?.thumbnail;
            const categoryName = product.categoryName || getBasic(product).category || product.categorySlug || 'Uncategorised';
            const productName = getProductTypeLabel(product) || product.productSlug || product.productName || parentId;

            map.set(key, {
                key,
                label: `${categoryName} / ${productName}`,
                categoryId: product.categoryId,
                categorySlug: product.categorySlug,
                categoryName,
                parentId,
                productSlug: product.productSlug || parentId,
                productName,
                imageBasePath: getBaseImagePathFromSample(sampleImage),
            });
        });

        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [draftProducts]);

    const categories = categorySummaries;

    const categoryColorMap = useMemo(
        () => Object.fromEntries(categorySummaries.map((category) => [category.key, category.color])),
        [categorySummaries],
    );

    const filteredProducts = useMemo(() => {
        const q = query.trim().toLowerCase();
        const matches = productsForStatusFilter.filter((product) => {
            const inventoryId = getMetaData(product).inventoryId?.trim() || '';
            const inventoryPass =
                inventoryFilter === 'all'
                    ? true
                    : inventoryFilter === 'with'
                        ? Boolean(inventoryId)
                        : !inventoryId;

            const categoryKey = product.categoryId || product.parentId || 'uncategorised';
            const typeKey = product.productId || product.parentId || product.productSlug || product.slug || product.id;
            const categoryPass = categoryFilter === 'all' || categoryFilter === categoryKey;
            const categorySelectionPass = !selectedCategoryKeys.length || selectedCategoryKeys.includes(categoryKey);
            const typeSelectionPass = !selectedTypeKeys.length || selectedTypeKeys.includes(typeKey);
            const flagsPass =
                (!flagFilters.isFeatured || getFlagValue(product, 'isFeatured')) &&
                (!flagFilters.isClearance || getFlagValue(product, 'isClearance')) &&
                (!flagFilters.isAusMade || getFlagValue(product, 'isAusMade')) &&
                (!flagFilters.isInStock || getFlagValue(product, 'isInStock'));

            if (!inventoryPass || !categoryPass || !categorySelectionPass || !typeSelectionPass || !flagsPass) return false;
            if (!q) return true;

            const haystack = [
                product.parentId,
                product.categoryName,
                product.title,
                product.name,
                product.description,
                getMetaData(product).inventoryId,
                ...(product.features || []),
                ...(product.searchTerms || []),
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();

            return haystack.includes(q);
        });

        const sorted = [...matches].sort((a, b) => {
            const direction = sortDirection === 'asc' ? 1 : -1;
            const aName = getDisplayName(a).toLowerCase();
            const bName = getDisplayName(b).toLowerCase();
            const aPrice = ensureAtLeastOnePrice(a.pricing)[0]?.price ?? -1;
            const bPrice = ensureAtLeastOnePrice(b.pricing)[0]?.price ?? -1;
            const aInventory = getMetaData(a).inventoryId || '';
            const bInventory = getMetaData(b).inventoryId || '';
            const aSort = a.sortOrder ?? 9999;
            const bSort = b.sortOrder ?? 9999;
            const aFeatures = a.features?.length ?? 0;
            const bFeatures = b.features?.length ?? 0;
            const aTerms = a.searchTerms?.length ?? 0;
            const bTerms = b.searchTerms?.length ?? 0;

            switch (sortKey) {
                case 'price':
                    return (aPrice - bPrice) * direction;
                case 'inventoryId':
                    return aInventory.localeCompare(bInventory) * direction;
                case 'featuresCount':
                    return (aFeatures - bFeatures) * direction;
                case 'searchTermsCount':
                    return (aTerms - bTerms) * direction;
                case 'sortOrder':
                    return (aSort - bSort) * direction;
                case 'name':
                default:
                    return aName.localeCompare(bName) * direction;
            }
        });

        return sorted;
    }, [categoryFilter, flagFilters, inventoryFilter, productsForStatusFilter, query, selectedCategoryKeys, selectedTypeKeys, sortDirection, sortKey]);


    const saveSummary = useMemo(() => {
        const originalProducts = originalProductsRef.current;

        const originalMap = new Map(originalProducts.map((product) => [product.id, product]));
        const draftMap = new Map(draftProducts.map((product) => [product.id, product]));

        const added = draftProducts.filter((product) => !originalMap.has(product.id));
        const physicallyRemoved = originalProducts.filter((product) => !draftMap.has(product.id));
        const removedByToggle = draftProducts.filter((product) => {
            const original = originalMap.get(product.id);
            if (!original) return false;
            return Boolean(getBasic(original).active ?? true) && getIsRemoved(product);
        });
        const removed = [...physicallyRemoved, ...removedByToggle.filter((product) => !physicallyRemoved.some((item) => item.id === product.id))];
        const removedIds = new Set(removed.map((product) => product.id));

        const edited = draftProducts.filter((product) => {
            const original = originalMap.get(product.id);
            if (!original || removedIds.has(product.id)) return false;
            return JSON.stringify(normalizeProductForDiff(original)) !== JSON.stringify(normalizeProductForDiff(product));
        });

        return {
            added,
            removed,
            edited,
            total: added.length + removed.length + edited.length,
        };
    }, [draftProducts]);

    const totals = useMemo(() => {
        const active = draftProducts.filter((item) => !getIsRemoved(item)).length;
        const deactivated = draftProducts.length - active;
        const withInventory = productsForStatusFilter.filter((item) => getMetaData(item).inventoryId?.trim()).length;
        const withPrices = productsForStatusFilter.filter((item) => ensureAtLeastOnePrice(item.pricing).some((price) => (price.price ?? 0) > 0)).length;
        return {
            all: draftProducts.length,
            active,
            deactivated,
            filtered: filteredProducts.length,
            withInventory,
            withoutInventory: productsForStatusFilter.length - withInventory,
            withPrices,
        };
    }, [draftProducts, filteredProducts.length, productsForStatusFilter]);

    const setProductsAndNotify = (nextProducts: ProductTableProduct[]) => {
        setDraftProducts(nextProducts);
        setHasChanges(true);
        onProductsChange?.(nextProducts);
    };

    const patchProduct = (productId: string, updater: (product: ProductTableProduct) => ProductTableProduct) => {
        const nextProducts = draftProducts.map((item) => (item.id === productId ? updater(item) : item));
        setProductsAndNotify(nextProducts);
    };

    const handleCellSave = (
        productId: string,
        field: 'name' | 'description' | 'searchTerms' | 'features' | 'inventoryId' | 'width' | 'height' | 'depth' | 'length',
        value: string,
    ) => {
        patchProduct(productId, (product) => {
            switch (field) {
                case 'name':
                    return { ...product, name: value, basic: { ...getBasic(product), name: value } };
                case 'description':
                    return { ...product, description: value };
                case 'searchTerms':
                    return {
                        ...product,
                        searchTerms: value
                            .split('\n')
                            .map((item) => item.trim())
                            .filter(Boolean),
                    };
                case 'features':
                    return {
                        ...product,
                        features: value
                            .split('\n')
                            .map((item) => item.trim())
                            .filter(Boolean),
                    };
                case 'inventoryId':
                    return {
                        ...product,
                        metaData: {
                            ...getMetaData(product),
                            inventoryId: value,
                        },
                    };
                case 'width':
                case 'height':
                case 'depth':
                case 'length':
                    return {
                        ...product,
                        attributes: {
                            ...getAttributes(product),
                            dimensions: {
                                ...getDimensions(product),
                                [field]: value,
                            },
                        },
                    };
                default:
                    return product;
            }
        });
    };

    const updatePricingItem = (productId: string, index: number, patch: Partial<ProductTablePrice>) => {
        patchProduct(productId, (product) => {
            const pricing = [...ensureAtLeastOnePrice(product.pricing)];
            pricing[index] = { ...pricing[index], ...patch };
            return { ...product, pricing };
        });
    };

    const addPricingItem = (productId: string) => {
        patchProduct(productId, (product) => ({
            ...product,
            pricing: [...ensureAtLeastOnePrice(product.pricing), { label: 'Option', price: null }],
        }));
    };

    const removePricingItem = (productId: string, index: number) => {
        patchProduct(productId, (product) => {
            const pricing = [...ensureAtLeastOnePrice(product.pricing)];
            if (pricing.length <= 1) return product;
            pricing.splice(index, 1);
            return { ...product, pricing };
        });
    };




    const toggleFlag = (productId: string, key: ProductFlagKey, checked: boolean) => {
        patchProduct(productId, (product) => ({
            ...product,
            flags: {
                ...getFlags(product),
                [key]: checked,
            },
        }));
    };

    const toggleRemoved = (productId: string, checked: boolean) => {
        patchProduct(productId, (product) => ({
            ...product,
            basic: {
                ...getBasic(product),
                active: !checked,
            },
        }));
    };

    const requestDeleteProduct = (product: ProductTableProduct) => {
        setPendingDeleteProduct(product);
    };

    const confirmDeleteProduct = async () => {
        if (!pendingDeleteProduct) return;

        const productToDelete = pendingDeleteProduct;
        setPendingDeleteProduct(null);
        setProductsAndNotify(draftProducts.filter((product) => product.id !== productToDelete.id));

        try {
            await onDeleteProduct?.(productToDelete);
        } catch (error) {
            console.error('Product delete callback failed', error);
        }
    };

    const toggleFlagFilter = (key: keyof typeof flagFilters, checked: boolean) => {
        setFlagFilters((current) => ({ ...current, [key]: checked }));
    };

    const toggleCategorySelection = (key: string, checked: boolean) => {
        setSelectedCategoryKeys((current) => (checked ? [...current, key] : current.filter((item) => item !== key)));
    };

    const toggleTypeSelection = (key: string, checked: boolean) => {
        setSelectedTypeKeys((current) => (checked ? [...current, key] : current.filter((item) => item !== key)));
    };

    const handleOpenLightbox = (product: ProductTableProduct) => {
        const slides = (product.images?.length ? product.images : [getPrimaryImage(product)])
            .filter(Boolean)
            .map((image) => ({ src: image!.previewUrl || image!.original || image!.thumbnail || '', alt: image!.alt || getDisplayName(product) }))
            .filter((slide) => Boolean(slide.src));

        if (!slides.length) return;
        setLightboxSlides(slides);
        setLightboxOpen(true);
    };

    const buildSavePayload = () => buildProductTableSavePayload({
        draftProducts,
        saveSummary,
    });

    const downloadLocalJsonRecords = () => {
        const payload = buildSavePayload();
        downloadProductTableDebugFiles({
            payload,
            draftProducts,
            fileNamePrefix,
        });
    };

    const saveRecords = async () => {
        const payload = buildSavePayload();

        savingStartedAtRef.current = Date.now();
        setSavingElapsedSeconds(0);
        setIsSaving(true);
        setSaveError(null);
        setSaveSuccess(null);

        try {
            await onSave?.(payload);

            originalProductsRef.current = draftProducts;

            setHasChanges(false);
            setSaveSuccess('Product database save payload sent successfully.');
        } catch (error) {
            setSaveError(error instanceof Error ? error.message : 'Failed to save product database.');
        } finally {
            const startedAt = savingStartedAtRef.current;
            if (startedAt) {
                setSavingElapsedSeconds(Math.max(1, Math.ceil((Date.now() - startedAt) / 1000)));
            }
            setIsSaving(false);
        }
    };
    const handleSaveClick = () => {
        setSaveError(null);
        setSaveSuccess(null);
        setShowSaveModal(true);
    };


    const openAddProductModal = () => {
        setNewProductForm(createEmptyProductForm());
        setShowAddModal(true);
    };

    const updateNewProductForm = (patch: Partial<NewProductFormState>) => {
        setNewProductForm((current) => ({ ...current, ...patch }));
    };

    const updateNewProductPrice = (index: number, patch: Partial<NewProductPriceFormState>) => {
        setNewProductForm((current) => {
            const prices = [...current.prices];
            prices[index] = { ...prices[index], ...patch };
            return { ...current, prices };
        });
    };

    const addNewProductPrice = () => {
        setNewProductForm((current) => ({
            ...current,
            prices: [...current.prices, { label: 'Option', price: '' }],
        }));
    };

    const removeNewProductPrice = (index: number) => {
        setNewProductForm((current) => {
            if (current.prices.length <= 1) return current;
            const prices = [...current.prices];
            prices.splice(index, 1);
            return { ...current, prices };
        });
    };

    const updateNewProductImage = (index: number, patch: Partial<NewProductImageFormState>) => {
        setNewProductForm((current) => {
            const images = [...current.images];
            images[index] = { ...images[index], ...patch };
            return { ...current, images };
        });
    };

    const addNewProductImage = () => {
        setNewProductForm((current) => ({
            ...current,
            images: [...current.images, { source: '', alt: '' }],
        }));
    };

    const addNewProductImageFiles = (files: FileList | File[]) => {
        const imageFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));
        if (!imageFiles.length) return;

        setNewProductForm((current) => ({
            ...current,
            images: [
                ...current.images,
                ...imageFiles.map((file) => ({
                    source: file.name,
                    alt: current.name || file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' '),
                    file,
                    previewUrl: URL.createObjectURL(file),
                })),
            ],
        }));
    };

    const removeNewProductImage = (index: number) => {
        setNewProductForm((current) => {
            const images = [...current.images];
            const removed = images[index];
            if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
            images.splice(index, 1);
            return { ...current, images };
        });
    };

    const selectedNewProductCategory = useMemo(() => {
        return categoryOptions.find((category) => category.key === newProductForm.categoryKey) || categoryOptions[0];
    }, [categoryOptions, newProductForm.categoryKey]);

    const generatedNewProductSlug = slugifyValue(newProductForm.slugOverride || newProductForm.name || '');
    const generatedNewProductId = useMemo(() => {
        const prefix = selectedNewProductCategory?.parentId || selectedNewProductCategory?.productSlug || selectedNewProductCategory?.categoryId || 'itm-prd-product';
        const cleanPrefix = slugifyValue(prefix) || 'itm-prd-product';
        const number = getNextNumberForPrefix(draftProducts, cleanPrefix);
        return `${cleanPrefix}-${number}`;
    }, [draftProducts, selectedNewProductCategory]);

    const handleAddProduct = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const name = newProductForm.name.trim() || 'Untitled product';
        const slug = slugifyValue(newProductForm.slugOverride || name);
        const selectedCategory = selectedNewProductCategory;
        const idPrefix = selectedCategory?.parentId || selectedCategory?.productSlug || selectedCategory?.categoryId || `itm-prd-${slug}`;
        const nextNumber = getNextNumberForPrefix(draftProducts, slugifyValue(idPrefix));
        const id = makeUniqueId(draftProducts, `${slugifyValue(idPrefix)}-${nextNumber}`);

        const pricing = newProductForm.prices
            .map((price) => {
                const priceNumber = price.price.trim() === '' ? null : Number(price.price);
                const safePrice = typeof priceNumber === 'number' && !Number.isNaN(priceNumber) ? priceNumber : null;
                return { label: price.label.trim() || 'Option', price: safePrice };
            })
            .filter((price, index) => index === 0 || price.label.trim() || price.price !== null);

        const imageInputs = newProductForm.images
            .map((image) => ({
                source: image.source.trim(),
                alt: image.alt.trim() || name,
                file: image.file,
                previewUrl: image.previewUrl,
            }))
            .filter((image) => image.source || image.file);

        const imageBasePath = selectedCategory?.imageBasePath || '';
        const uploadedImages = imageInputs
            .map((image) => image.file)
            .filter((file): file is File => Boolean(file));

        const images = imageInputs.map((image, index) => {
            const imageNumber = index + 1;
            const extension = image.file ? getFileExtensionFromFile(image.file) : getFileExtension(image.source);
            const fileName = `${id}-${imageNumber}.${extension}`;
            const original = imageBasePath ? `${imageBasePath}/${fileName}` : fileName;
            const thumbnail = imageBasePath ? `${imageBasePath}/thumbnails/${fileName}` : fileName;

            return {
                id: `${id}-image-${imageNumber}`,
                original,
                thumbnail,
                alt: image.alt,
                source: image.source,
                generatedFileName: fileName,
                sourceFileName: image.file?.name,
                sourceFile: image.file,
                previewUrl: image.previewUrl,
            };
        });

        const nextProduct: ProductTableProduct = {
            id,
            name,
            title: name,
            slug,
            description: newProductForm.description.trim(),
            categoryId: selectedCategory?.categoryId,
            categorySlug: selectedCategory?.categorySlug,
            categoryName: selectedCategory?.categoryName,
            parentId: selectedCategory?.parentId,
            productSlug: selectedCategory?.productSlug,
            productName: selectedCategory?.productName,
            pricing: pricing.length ? pricing : [{ label: 'Starting from', price: null }],
            features: splitLines(newProductForm.features),
            searchTerms: splitLines(newProductForm.searchTerms),
            attributes: { dimensions: { width: newProductForm.width.trim(), height: newProductForm.height.trim(), depth: newProductForm.depth.trim(), length: newProductForm.length.trim() } },
            metaData: { productId: id.replace(/-/g, ''), inventoryId: newProductForm.inventoryId.trim() },
            flags: { isFeatured: newProductForm.isFeatured, isClearance: newProductForm.isClearance, isAusMade: newProductForm.isAusMade, isInStock: newProductForm.isInStock },
            ...(uploadedImages.length ? { uploadedImages } : {}),
            ...(images.length ? { imageUrl: images[0].original, imageAlt: images[0].alt, featureImage: images[0], images } : {}),
        };

        setProductsAndNotify([...draftProducts, nextProduct]);
        onAddProduct?.(nextProduct);
        setShowAddModal(false);
        setNewProductForm(createEmptyProductForm());
    };

    const toggleSort = (nextKey: SortKey) => {
        if (sortKey === nextKey) {
            setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
            return;
        }
        setSortKey(nextKey);
        setSortDirection('asc');
    };

    const renderEditable = ({
                                product,
                                field,
                                display,
                                rawValue,
                                rows = 2,
                                isCode = false,
                            }: {
        product: ProductTableProduct;
        field: 'name' | 'description' | 'searchTerms' | 'features' | 'inventoryId' | 'width' | 'height' | 'depth' | 'length';
        display: React.ReactNode;
        rawValue: string;
        rows?: number;
        isCode?: boolean;
    }) => {
        const key = `${product.id}:${field}`;
        const editing = editingCell === key;

        if (editing) {
            return (
                <textarea
                    autoFocus
                    rows={rows}
                    defaultValue={rawValue}
                    style={{ ...styles.textarea, ...(isCode ? styles.mono : null) }}
                    onBlur={(event) => {
                        handleCellSave(product.id, field, event.currentTarget.value);
                        setEditingCell(null);
                    }}
                    onKeyDown={(event) => {
                        if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                            handleCellSave(product.id, field, (event.currentTarget as HTMLTextAreaElement).value);
                            setEditingCell(null);
                        }
                        if (event.key === 'Escape') {
                            setEditingCell(null);
                        }
                    }}
                />
            );
        }

        return (
            <button type="button" style={styles.cellButton} onClick={() => setEditingCell(key)}>
                <div style={styles.editableBox}>{display}</div>
            </button>
        );
    };


    return {
        query,
        setQuery,
        categoryFilter,
        setCategoryFilter,
        inventoryFilter,
        setInventoryFilter,
        flagFilters,
        setFlagFilters,
        selectedCategoryKeys,
        setSelectedCategoryKeys,
        selectedTypeKeys,
        setSelectedTypeKeys,
        statusFilter,
        setStatusFilter,
        showCountFilters,
        setShowCountFilters,
        sortKey,
        setSortKey,
        sortDirection,
        setSortDirection,
        lightboxSlides,
        lightboxOpen,
        setLightboxOpen,
        showSaveModal,
        setShowSaveModal,
        showAddModal,
        setShowAddModal,
        pendingDeleteProduct,
        setPendingDeleteProduct,
        isSaving,
        savingElapsedSeconds,
        saveError,
        saveSuccess,
        newProductForm,
        imageFileInputRef,
        categorySummaries,
        productTypeSummaries,
        categoryOptions,
        categories,
        categoryColorMap,
        filteredProducts,
        saveSummary,
        totals,
        toggleCategorySelection,
        toggleTypeSelection,
        toggleFlagFilter,
        handleOpenLightbox,
        handleSaveClick,
        openAddProductModal,
        downloadLocalJsonRecords,
        updateNewProductForm,
        selectedNewProductCategory,
        generatedNewProductSlug,
        generatedNewProductId,
        handleAddProduct,
        updateNewProductPrice,
        addNewProductPrice,
        removeNewProductPrice,
        addNewProductImageFiles,
        removeNewProductImage,
        updateNewProductImage,
        addNewProductImage,
        toggleSort,
        renderEditable,
        toggleRemoved,
        requestDeleteProduct,
        confirmDeleteProduct,
        updatePricingItem,
        addPricingItem,
        removePricingItem,
        toggleFlag,
        saveRecords,
    };
};
